import React, { useState, useRef } from 'react';
import { audioBufferToWavBlob, audioBufferToMp3Blob, applyMasterBus, downloadBlob } from '../../lib/audio-export';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import LoudnessTargetSelector from './components/LoudnessTargetSelector';
import MasteringMeter from './components/MasteringMeter';
import ABPreview from './components/ABPreview';
import ExportOptions from './components/ExportOptions';

const audioBufferToWav = (buffer, bitDepth = 24) => {
  const numberOfChannels = buffer?.numberOfChannels || 2;
  const sampleRate = buffer?.sampleRate || 48000;
  const bytesPerSample = bitDepth / 8;
  const dataLength = buffer.length * numberOfChannels * bytesPerSample;
  const result = new ArrayBuffer(44 + dataLength);
  const view = new DataView(result);
  let offset = 0;

  const writeString = (value) => {
    for (let i = 0; i < value.length; i += 1) view.setUint8(offset + i, value.charCodeAt(i));
    offset += value.length;
  };

  writeString('RIFF');
  view.setUint32(offset, 36 + dataLength, true); offset += 4;
  writeString('WAVE');
  writeString('fmt ');
  view.setUint32(offset, 16, true); offset += 4;
  view.setUint16(offset, 1, true); offset += 2;
  view.setUint16(offset, numberOfChannels, true); offset += 2;
  view.setUint32(offset, sampleRate, true); offset += 4;
  view.setUint32(offset, sampleRate * numberOfChannels * bytesPerSample, true); offset += 4;
  view.setUint16(offset, numberOfChannels * bytesPerSample, true); offset += 2;
  view.setUint16(offset, bitDepth, true); offset += 2;
  writeString('data');
  view.setUint32(offset, dataLength, true); offset += 4;

  for (let i = 0; i < buffer.length; i += 1) {
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      const channelData = buffer.getChannelData(Math.min(channel, buffer.numberOfChannels - 1));
      const sample = Math.max(-1, Math.min(1, channelData[i] || 0));
      if (bitDepth === 24) {
        const value = Math.round(sample < 0 ? sample * 0x800000 : sample * 0x7fffff);
        view.setUint8(offset, value & 0xff);
        view.setUint8(offset + 1, (value >> 8) & 0xff);
        view.setUint8(offset + 2, (value >> 16) & 0xff);
        offset += 3;
      } else {
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
      }
    }
  }

  return new Blob([result], { type: 'audio/wav' });
};

const sanitizeFileName = (value, fallback = 'makingitmix-master') =>
  (value || fallback).trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || fallback;

const MasteringPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const masteringInput = location?.state?.masteringInput || null;
  const [selectedTarget, setSelectedTarget] = useState('streaming');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [exportFormat, setExportFormat] = useState('wav-mp3');
  const [metadata, setMetadata] = useState({
    artist: '',
    title: '',
    album: ''
  });
  const [audioMetrics, setAudioMetrics] = useState({
    currentLUFS: -18.5,
    peakLevel: -3.2,
    dynamicRange: 8.5,
    stereoCorrelation: 0.85
  });
  const [normalizationApplied, setNormalizationApplied] = useState(false);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const masteredBufferRef = useRef(null);
  const exportBuffer = masteredBufferRef.current || audioBufferRef.current;
  const exportSampleRate = exportBuffer?.sampleRate || 48000;
  const exportBitDepth = 24;
  const estimatedExportSize = exportBuffer
    ? 44 + (exportBuffer.length * (exportBuffer.numberOfChannels || 2) * (exportBitDepth / 8))
    : null;

  const getChannelGain = (channel) => {
    const db = Number(channel?.level);
    if (Number.isFinite(db)) return Math.max(0, Math.min(2, Math.pow(10, db / 20)));
    return 0.8;
  };

  const loadAudioBuffer = async (context, sourceUrl) => {
    if (!sourceUrl) return null;
    const response = await fetch(sourceUrl);
    if (!response.ok) throw new Error(`Could not load mix source: ${sourceUrl}`);
    return context.decodeAudioData(await response.arrayBuffer());
  };

  const createMockAudioBuffer = (context, duration = 3) => {
    const sampleRate = context.sampleRate || 48000;
    const mockBuffer = context.createBuffer(2, Math.ceil(sampleRate * duration), sampleRate);
    for (let channel = 0; channel < mockBuffer.numberOfChannels; channel++) {
      const channelData = mockBuffer.getChannelData(channel);
      for (let i = 0; i < channelData.length; i++) {
        channelData[i] = Math.sin(2 * Math.PI * 440 * i / sampleRate) * 0.7;
      }
    }
    return mockBuffer;
  };

  const applyMasterGain = async (sourceBuffer, gainMultiplier = 1) => {
    const sampleRate = sourceBuffer.sampleRate || 48000;
    const offlineContext = new OfflineAudioContext(
      sourceBuffer.numberOfChannels,
      sourceBuffer.length,
      sampleRate
    );
    const source = offlineContext.createBufferSource();
    const gain = offlineContext.createGain();
    source.buffer = sourceBuffer;
    gain.gain.value = Math.max(0, Number(gainMultiplier) || 1);
    source.connect(gain);
    gain.connect(offlineContext.destination);
    source.start(0);
    return offlineContext.startRendering();
  };

  const renderInputMixBuffer = async () => {
    const inputChannels = masteringInput?.channels || [];
    const channelsWithAudio = inputChannels.filter((channel) => Array.isArray(channel?.audioClips) && channel.audioClips.length);
    if (!channelsWithAudio.length) {
      const context = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = context;
      return createMockAudioBuffer(context, 3);
    }

    const sampleRate = 48000;
    const duration = Math.max(1, Number(masteringInput?.duration) || 180);
    const offlineContext = new OfflineAudioContext(2, Math.ceil(duration * sampleRate), sampleRate);

    for (const channel of channelsWithAudio) {
      const channelGainValue = getChannelGain(channel);
      const pan = Number(channel?.pan) || 0;
      const normalizedPan = Math.max(-1, Math.min(1, Math.abs(pan) > 1 ? pan / 50 : pan));

      for (const clip of channel.audioClips) {
        try {
          const buffer = await loadAudioBuffer(offlineContext, clip?.sourceUrl);
          if (!buffer) continue;

          const clipStart = Math.max(0, Number(clip?.clipStart) || 0);
          const clipEnd = Math.max(clipStart, Number(clip?.clipEnd ?? buffer.duration) || buffer.duration);
          const clipOffset = Math.max(0, Number(clip?.clipOffset) || 0);
          const sourceOffset = Math.min(clipStart, buffer.duration);
          const sourceDuration = Math.min(clipEnd - clipStart, buffer.duration - sourceOffset, duration - clipOffset);
          if (sourceDuration <= 0.01) continue;

          const source = offlineContext.createBufferSource();
          const gain = offlineContext.createGain();
          const panner = offlineContext.createStereoPanner();
          source.buffer = buffer;
          gain.gain.value = channelGainValue;
          panner.pan.value = normalizedPan;
          source.connect(gain);
          gain.connect(panner);
          panner.connect(offlineContext.destination);
          source.start(clipOffset, sourceOffset, sourceDuration);
        } catch (error) {
          console.error('Mastering mix source failed:', error);
        }
      }
    }

    return offlineContext.startRendering();
  };

  const loudnessTargets = [
    {
      id: 'streaming',
      name: 'Streaming',
      lufs: -14,
      description: 'Optimized for Spotify, Apple Music, YouTube',
      icon: 'Radio',
      color: 'var(--color-accent)'
    },
    {
      id: 'loud',
      name: 'Loud',
      lufs: -9,
      description: 'High-energy commercial sound',
      icon: 'Volume2',
      color: 'var(--color-warning)'
    },
    {
      id: 'club',
      name: 'Club',
      lufs: -6,
      description: 'Maximum loudness for club systems',
      icon: 'Zap',
      color: 'var(--color-error)'
    }
  ];

  // Audio normalization function
  const normalizeAudio = async (audioBuffer, targetLUFS) => {
    try {
      const channelData = audioBuffer?.getChannelData(0);
      const sampleRate = audioBuffer?.sampleRate;
      
      // Calculate RMS (Root Mean Square) for loudness estimation
      let sumSquares = 0;
      for (let i = 0; i < channelData?.length; i++) {
        sumSquares += channelData?.[i] * channelData?.[i];
      }
      const rms = Math.sqrt(sumSquares / channelData?.length);
      
      // Convert RMS to dB (approximate LUFS)
      const currentLUFS = 20 * Math.log10(rms) - 0.691; // K-weighting approximation
      
      // Find peak level
      let peakLevel = 0;
      for (let i = 0; i < channelData?.length; i++) {
        const absValue = Math.abs(channelData?.[i]);
        if (absValue > peakLevel) peakLevel = absValue;
      }
      const peakdB = 20 * Math.log10(peakLevel);
      
      // Calculate required gain adjustment
      const gainAdjustmentdB = targetLUFS - currentLUFS;
      const gainMultiplier = Math.pow(10, gainAdjustmentdB / 20);
      
      // Check if normalization would cause clipping
      const newPeakLevel = peakLevel * gainMultiplier;
      const headroom = 0.1; // -0.5 dB headroom to prevent clipping
      
      let finalGainMultiplier = gainMultiplier;
      if (newPeakLevel > (1.0 - headroom)) {
        // Reduce gain to prevent clipping
        finalGainMultiplier = (1.0 - headroom) / peakLevel;
        console.log('Gain reduced to prevent clipping');
      }
      
      // Calculate final metrics
      const finalPeakLevel = peakLevel * finalGainMultiplier;
      const finalPeakdB = 20 * Math.log10(finalPeakLevel);
      const finalLUFS = currentLUFS + (20 * Math.log10(finalGainMultiplier));
      
      return {
        currentLUFS: parseFloat(currentLUFS?.toFixed(1)),
        targetLUFS,
        finalLUFS: parseFloat(finalLUFS?.toFixed(1)),
        peakLevel: parseFloat(peakdB?.toFixed(1)),
        finalPeakLevel: parseFloat(finalPeakdB?.toFixed(1)),
        gainAdjustmentdB: parseFloat(gainAdjustmentdB?.toFixed(1)),
        gainMultiplier: finalGainMultiplier,
        clippingPrevented: newPeakLevel > (1.0 - headroom)
      };
    } catch (error) {
      console.error('Normalization error:', error);
      return null;
    }
  };

  const handleStartMastering = async () => {
    setIsProcessing(true);
    setProcessingProgress(0);
    setNormalizationApplied(false);
    
    try {
      // Initialize audio context if needed
      if (!audioContextRef?.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const targetLUFS = loudnessTargets?.find(t => t?.id === selectedTarget)?.lufs || -14;
      
      // Load/render actual mix audio when arriving from Fine Tune Mix; fallback to demo audio for direct visits.
      setProcessingProgress(10);
      await new Promise(resolve => setTimeout(resolve, 300));

      const mixBuffer = await renderInputMixBuffer();
      audioBufferRef.current = mixBuffer;
      masteredBufferRef.current = null;
      setProcessingProgress(30);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Perform audio normalization
      const normalizationResult = await normalizeAudio(mixBuffer, targetLUFS);
      
      if (normalizationResult) {
        setProcessingProgress(60);
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Update metrics with normalized values
        setAudioMetrics({
          currentLUFS: normalizationResult?.finalLUFS,
          peakLevel: normalizationResult?.finalPeakLevel,
          dynamicRange: 8.5,
          stereoCorrelation: 0.85
        });
        
        setNormalizationApplied(true);
        masteredBufferRef.current = await applyMasterGain(mixBuffer, normalizationResult?.gainMultiplier || 1);
        console.log('Normalization applied:', normalizationResult);
        
        setProcessingProgress(80);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Complete processing
      setProcessingProgress(100);
      await new Promise(resolve => setTimeout(resolve, 300));
      
    } catch (error) {
      console.error('Mastering error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReturnToMix = () => {
    navigate('/fine-tune-mix-page', masteringInput ? {
      state: { sessionMixData: masteringInput?.sessionMixData },
    } : undefined);
  };

  const handleExport = async () => {
    try {
      const exportBuffer = masteredBufferRef.current || audioBufferRef.current;
      if (!exportBuffer) {
        throw new Error('Run mastering before exporting.');
      }

      // Apply master bus DSP: bus EQ + LUFS targeting + true-peak limiter
      const target = loudnessTargets?.find(t => t?.id === selectedTarget)?.lufs ?? -14;
      const { buffer: finalBuffer, metrics } = await applyMasterBus(exportBuffer, { targetLUFS: target });
      console.log('Master metrics:', metrics);

      const title = metadata?.title || masteringInput?.sessionTitle || 'makingitmix-master';
      const baseName = sanitizeFileName(title);
      const wantsWav = exportFormat === 'wav' || exportFormat === 'wav-mp3';
      const wantsMp3 = exportFormat === 'mp3' || exportFormat === 'wav-mp3';

      let wavBlob = null;
      let mp3Blob = null;
      if (wantsWav) {
        wavBlob = audioBufferToWavBlob(finalBuffer);
        downloadBlob(wavBlob, `${baseName}-master.wav`);
      }
      if (wantsMp3) {
        mp3Blob = await audioBufferToMp3Blob(finalBuffer, 320);
        downloadBlob(mp3Blob, `${baseName}-master.mp3`);
      }

      const estimatedFileSize = (wavBlob?.size || 0) + (mp3Blob?.size || 0);

      // Log export start
      const { error: logStartError } = user ? await supabase
        ?.from('export_logs')
        ?.insert({
          user_id: user?.id,
          session_id: masteringInput?.sessionMixData?.sessionId || null,
          action: 'export_started',
          export_format: 'wav',
          status: 'processing',
          metadata: {
            artist: metadata?.artist,
            title: metadata?.title,
            album: metadata?.album,
            loudness_target: selectedTarget,
            requested_format: exportFormat,
            file_name: fileName
          }
        }) : { error: null };

      if (logStartError) {
        console.error('Error logging export start:', logStartError);
      }

      // Create mastering export record
      const { data: exportData, error: exportError } = user ? await supabase
        ?.from('mastering_exports')
        ?.insert({
          user_id: user?.id,
          session_id: masteringInput?.sessionMixData?.sessionId || null,
          export_format: 'wav',
          file_size: estimatedFileSize,
          loudness_target: selectedTarget,
          metadata: {
            artist: metadata?.artist,
            title: metadata?.title,
            album: metadata?.album,
            current_lufs: audioMetrics?.currentLUFS,
            peak_level: audioMetrics?.peakLevel,
            dynamic_range: audioMetrics?.dynamicRange,
            requested_format: exportFormat,
            file_name: fileName
          }
        })
        ?.select()
        ?.single() : { data: null, error: null };

      if (exportError) {
        console.error('Error logging master export:', exportError);
      }

      // Log export completion
      const { error: logCompleteError } = user ? await supabase
        ?.from('export_logs')
        ?.insert({
          user_id: user?.id,
          session_id: masteringInput?.sessionMixData?.sessionId || null,
          export_id: exportData?.id,
          action: 'export_completed',
          export_format: 'wav',
          file_size: estimatedFileSize,
          status: 'success',
          metadata: {
            artist: metadata?.artist,
            title: metadata?.title,
            album: metadata?.album,
            file_name: fileName
          }
        }) : { error: null };

      if (logCompleteError) {
        console.error('Error logging export completion:', logCompleteError);
      }

      console.log('Master exported:', fileName);
    } catch (error) {
      console.error('Export error:', error);

      // Log export failure
      if (user) {
        await supabase
          ?.from('export_logs')
          ?.insert({
            user_id: user?.id,
            session_id: null,
            action: 'export_failed',
            export_format: exportFormat,
            status: 'error',
            error_message: error?.message || 'Unknown error',
            metadata: {
              artist: metadata?.artist,
              title: metadata?.title,
              album: metadata?.album
            }
          });
      }

      alert(error?.message || 'Export failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <div className="container-studio py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
                  Mastering
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Finalize your track with professional loudness targeting and comprehensive metering
                </p>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReturnToMix}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  <span className="hidden md:inline">Return to Mix</span>
                  <span className="md:hidden">Back</span>
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleStartMastering}
                  disabled={isProcessing}
                  loading={isProcessing}
                  iconName="Play"
                  iconPosition="left"
                >
                  <span className="hidden md:inline">{isProcessing ? 'Processing...' : 'Start Mastering'}</span>
                  <span className="md:hidden">{isProcessing ? 'Processing' : 'Master'}</span>
                </Button>
              </div>
            </div>

            {masteringInput && (
              <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-emerald-200">
                  <Icon name="CheckCircle2" size={16} color="#6ee7b7" />
                  <span>
                    Loaded mix from {masteringInput?.sessionTitle || 'Fine Tune Mix'} · {masteringInput?.channels?.filter((channel) => channel?.audioClips?.length)?.length || 0} audio channels
                  </span>
                </div>
              </div>
            )}

            {isProcessing && (
              <div className="mt-4 p-4 rounded-lg bg-accent/10 border border-accent/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon name="Loader" size={16} color="var(--color-accent)" className="animate-spin" />
                    <span className="text-sm font-medium">
                      {processingProgress < 30 ? 'Loading audio...' :
                       processingProgress < 60 ? 'Analyzing peaks...' :
                       processingProgress < 80 ? 'Normalizing audio...': 'Finalizing master...'}
                    </span>
                  </div>
                  <span className="text-sm font-data">{processingProgress}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all duration-300"
                    style={{ width: `${processingProgress}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {processingProgress < 100 ? 
                    `Estimated time remaining: ${Math.ceil((100 - processingProgress) / 20)} seconds` :
                    'Processing complete!'}
                </p>
              </div>
            )}
            
            {normalizationApplied && !isProcessing && (
              <div className="mt-4 p-4 rounded-lg bg-success/10 border border-success/20">
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                  <span className="text-sm font-medium text-success">
                    Audio normalized to {loudnessTargets?.find(t => t?.id === selectedTarget)?.lufs} LUFS - Ready for export
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <LoudnessTargetSelector
                targets={loudnessTargets}
                selectedTarget={selectedTarget}
                onSelectTarget={setSelectedTarget}
              />

              <MasteringMeter
                selectedTarget={loudnessTargets?.find(t => t?.id === selectedTarget)}
                isProcessing={isProcessing}
                audioMetrics={audioMetrics}
              />

              <ABPreview isProcessing={isProcessing} />
            </div>

            <div className="space-y-6">
              <ExportOptions
                format={exportFormat}
                onFormatChange={setExportFormat}
                metadata={metadata}
                onMetadataChange={setMetadata}
                onExport={handleExport}
                disabled={isProcessing || processingProgress < 100}
                sampleRate={exportSampleRate}
                bitDepth={exportBitDepth}
                estimatedSizeBytes={estimatedExportSize}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MasteringPage;
